import { type FC, useCallback } from 'react';
import { Button, type TestProps } from '@direct-frontend/components';
import { useFormContext, useFormInstance, useFormState } from '@adv-frontend/form-kit';
import { showError } from '../../../../lib/toast';
import { changeVariantPriceTooltipForm } from '../../model';
import { type ChangedVariantPriceOnSave } from '../../types';

type SaveButtonProps = {
    formId: string;
    isSaving: boolean;
    startSaving: VoidFunction;
    stopSaving: VoidFunction;
    onSave: ChangedVariantPriceOnSave;
    onSuccessSave: VoidFunction;
} & TestProps;

export const SaveButton: FC<SaveButtonProps> = ({
    formId,
    startSaving,
    stopSaving,
    isSaving,
    onSave,
    'data-testid': dataTestId = 'SaveButton',
    onSuccessSave,
}) => {
    const formInstance = useFormInstance({ form: changeVariantPriceTooltipForm, id: formId });
    const formState = useFormState({ form: changeVariantPriceTooltipForm, id: formId });
    const formContext = useFormContext({ form: changeVariantPriceTooltipForm, id: formId });
    const handleClick = useCallback(async () => {
        startSaving();

        try {
            const changedVariantPrice = await formInstance.submit();

            if (!changedVariantPrice) {
                showError({ label: 'Исправьте ошибки в форме' });

                return;
            }

            await onSave({
                id: formContext.id,
                price: changedVariantPrice.price,
                minimal_price: changedVariantPrice.minimalPrice,
                manual_discount_price: changedVariantPrice.manualDiscountPrice,
            });

            onSuccessSave();
        } catch (error) {
            showError({ label: 'Не удалось сохранить цену' }, error);
        } finally {
            stopSaving();
        }
    }, [formContext.id, formInstance, onSave, onSuccessSave, startSaving, stopSaving]);

    return (
        <Button
            isDisabled={formState.isSubmitting}
            isLoading={formState.isSubmitting || isSaving}
            onClick={handleClick}
            color="accent"
            size="xs"
            data-testid={`${dataTestId}.SaveButton`}
        >
            Сохранить
        </Button>
    );
};
